import { useState } from 'react';
import { Send, Loader2, ThumbsUp, ThumbsDown, Play, Pause } from 'lucide-react';
import { askQuestion, rateAnswer } from '../../services/helpService';
import { useGlobalAudio } from '../../context/GlobalAudioContext';
import { getStoredUser } from '../../context/AuthContext';

const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';

/**
 * FaqFreeQuestion — zone "Pose ta question" dans le FaqPanel.
 * Appelle /help/ask, affiche la réponse + audio + rating.
 */
const FaqFreeQuestion = () => {
    const { playAudio, stopAudio, isPlaying, currentUrl } = useGlobalAudio();
    const [question, setQuestion] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [rated, setRated] = useState(null);

    const handleAsk = async () => {
        if (!question.trim() || loading) return;
        setLoading(true);
        setError(null);
        setResult(null);
        setRated(null);
        try {
            const user = getStoredUser();
            const data = await askQuestion({
                question: question.trim(),
                language: 'fr',
                userUuid: user?.peopleUuid || ''
            });
            setResult(data);
            // Auto-play audio
            if (data.audiopath || data.audioPath) {
                const path = data.audiopath || data.audioPath;
                playAudio(`${FILES_BASE_URL}/${path}`, { anchor: 'bottom-right' });
            }
        } catch (err) {
            setError(err.message || 'Erreur inconnue');
        } finally {
            setLoading(false);
        }
    };

    const handleRate = async (rating) => {
        if (!result) return;
        const logUuid = result.loguuid || result.logUuid;
        if (!logUuid) return;
        try {
            await rateAnswer(logUuid, rating);
            setRated(rating);
        } catch (err) {
            console.warn('rate error', err?.message || err);
        }
    };

    const toggleAudio = () => {
        if (!result) return;
        const path = result.audiopath || result.audioPath;
        if (!path) return;
        const url = `${FILES_BASE_URL}/${path}`;
        if (isPlaying && currentUrl === url) {
            stopAudio();
        } else {
            playAudio(url, { anchor: 'bottom-right' });
        }
    };

    return (
        <div className="border border-purple-200 rounded-xl p-4 bg-gradient-to-br from-purple-50 to-pink-50 space-y-3">
            <div className="font-medium text-sm text-gray-900">Pose ta question librement</div>

            <div className="flex gap-2">
                <textarea
                    value={question}
                    onChange={e => setQuestion(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            handleAsk();
                        }
                    }}
                    rows={2}
                    placeholder="Ex: Comment partager mes livres avec mes voisins ?"
                    className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    disabled={loading}
                />
                <button
                    onClick={handleAsk}
                    disabled={!question.trim() || loading}
                    className="self-end px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    <span className="text-sm">Poser</span>
                </button>
            </div>

            {error && (
                <div className="text-sm text-red-600 bg-red-50 rounded-lg p-2">{error}</div>
            )}

            {result && (
                <div className="bg-white rounded-lg p-3 border border-purple-100">
                    <div className="text-xs text-gray-500 mb-2">
                        {result.source === 'faq' ? 'Réponse issue de la FAQ' : 'Réponse générée'}
                    </div>
                    <div className="text-sm text-gray-800 whitespace-pre-wrap">{result.answer}</div>

                    <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t">
                        <div className="flex items-center gap-2">
                            {(result.audiopath || result.audioPath) && (
                                <button
                                    onClick={toggleAudio}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-purple-600 text-white rounded-lg text-xs hover:bg-purple-700"
                                >
                                    {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                                    {isPlaying ? 'Pause' : 'Écouter'}
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500">Utile ?</span>
                            <button
                                onClick={() => handleRate(1)}
                                disabled={rated !== null}
                                className={`p-1 rounded ${rated === 1 ? 'text-green-600' : 'text-gray-400 hover:text-green-600'} disabled:cursor-not-allowed`}
                            >
                                <ThumbsUp className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => handleRate(-1)}
                                disabled={rated !== null}
                                className={`p-1 rounded ${rated === -1 ? 'text-red-600' : 'text-gray-400 hover:text-red-600'} disabled:cursor-not-allowed`}
                            >
                                <ThumbsDown className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FaqFreeQuestion;
